import { useEffect, useState, useCallback } from "react";
import { api, money, fmtDate } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AutoBilling from "@/components/AutoBilling";
import { toast } from "sonner";
import { Download, Send, GripVertical, FileText, ReceiptText, Repeat } from "lucide-react";

const BOARD = ["Awaiting Approval", "Ready for Invoicing", "Invoiced", "Paid"];
const COL_HINT = {
  "Awaiting Approval": "Completed work awaiting manager sign-off",
  "Ready for Invoicing": "Approved & complete — ready for an invoice",
  "Invoiced": "Invoice raised (requires invoice number)",
  "Paid": "Payment received",
};
const DATE_TYPES = [
  { v: "completion_date", l: "Completion date" }, { v: "creation_date", l: "Job creation date" },
  { v: "scheduled_date", l: "Scheduled date" }, { v: "approval_date", l: "Approval date" },
  { v: "sage_date", l: "Invoice date" },
];

export default function Billing() {
  const { company } = useTenant();
  const cur = company?.currency || "EUR";
  const [rows, setRows] = useState([]);
  const [drag, setDrag] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [invoiceDlg, setInvoiceDlg] = useState(null); // { job }
  const [invoiceNo, setInvoiceNo] = useState("");

  // export tab state
  const [sel, setSel] = useState({});
  const [filters, setFilters] = useState({ date_type: "completion_date", from_date: "", to_date: "" });
  const [batches, setBatches] = useState([]);
  const [confirmSent, setConfirmSent] = useState(null);
  const [sentContact, setSentContact] = useState("");

  const load = useCallback(() => {
    api.get("/billing/jobs").then((r) => setRows(r.data));
    api.get("/billing/batches").then((r) => setBatches(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const boardJobs = rows.filter((j) => j.board_stage);
  const byStage = (s) => boardJobs.filter((j) => j.board_stage === s);

  const move = async (job, stage, invoice_number) => {
    try {
      await api.post("/billing/move", { job_id: job.id, stage, invoice_number });
      toast.success(`${job.ref} → ${stage}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Move failed"); }
  };

  const onDrop = (stage) => {
    setOverCol(null);
    const job = drag; setDrag(null);
    if (!job || job.board_stage === stage) return;
    if (stage === "Invoiced") { setInvoiceDlg(job); setInvoiceNo(""); return; }
    move(job, stage);
  };

  const stageValue = (s) => byStage(s).reduce((a, j) => a + (j.expected_value || 0), 0);

  // export tab
  const filteredForExport = () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    return params;
  };
  const [exportRows, setExportRows] = useState([]);
  const loadExport = useCallback(() => { api.get("/billing/jobs", { params: filteredForExport() }).then((r) => setExportRows(r.data)); }, [filters]);
  useEffect(() => { loadExport(); }, [loadExport]);

  const selectedIds = Object.keys(sel).filter((k) => sel[k]);
  const doExport = async () => {
    if (!selectedIds.length) return toast.error("Select jobs to export");
    const { data } = await api.post("/billing/export", { job_ids: selectedIds, from_date: filters.from_date, to_date: filters.to_date, date_type: filters.date_type });
    const blob = new Blob([data.csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${data.ref}.csv`; a.click(); URL.revokeObjectURL(url);
    setConfirmSent(data); setSel({}); load();
  };
  const confirmSentAction = async (yes) => {
    if (yes && confirmSent) { await api.post(`/billing/batches/${confirmSent.id}/confirm-sent`, { accounts_contact: sentContact }); toast.success("Recorded as sent to accounts"); }
    setConfirmSent(null); setSentContact(""); load();
  };

  return (
    <div>
      <h1 className="text-2xl font-head font-bold text-slate-900 mb-1">Billing & Invoicing</h1>
      <p className="text-sm text-slate-500 mb-4">Drag jobs across the pipeline. Amden records invoicing status only — it does not create invoices in your accounting system.</p>

      <Tabs defaultValue="board">
        <TabsList>
          <TabsTrigger value="board" data-testid="billing-tab-board"><ReceiptText className="h-4 w-4 mr-1" />Invoicing Board</TabsTrigger>
          <TabsTrigger value="auto" data-testid="billing-tab-auto"><Repeat className="h-4 w-4 mr-1" />Auto Billing</TabsTrigger>
          <TabsTrigger value="export" data-testid="billing-tab-export"><FileText className="h-4 w-4 mr-1" />CSV Export & History ({batches.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="board" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {BOARD.map((stage) => (
              <div key={stage} data-testid={`board-col-${stage}`}
                onDragOver={(e) => { e.preventDefault(); setOverCol(stage); }}
                onDragLeave={() => setOverCol((c) => (c === stage ? null : c))}
                onDrop={() => onDrop(stage)}
                className={`rounded-md border ${overCol === stage ? "border-dashed accent-border bg-slate-50" : "border-slate-200 bg-slate-50/50"}`}>
                <div className="px-3 py-2.5 border-b border-slate-200 bg-white rounded-t-md">
                  <div className="flex items-center justify-between">
                    <StatusBadge status={stage} />
                    <span className="text-xs text-slate-400">{byStage(stage).length}</span>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1">{COL_HINT[stage]}</div>
                  <div className="text-xs font-medium text-slate-600 mt-0.5">{money(stageValue(stage), cur)}</div>
                </div>
                <div className="p-2 space-y-2 min-h-[200px]">
                  {byStage(stage).map((j) => (
                    <div key={j.id} data-testid={`board-card-${j.id}`} draggable
                      onDragStart={() => setDrag(j)} onDragEnd={() => { setDrag(null); setOverCol(null); }}
                      className="bg-white border border-slate-200 rounded-md p-2.5 cursor-grab active:cursor-grabbing hover:shadow-sm transition-shadow">
                      <div className="flex items-start gap-1.5">
                        <GripVertical className="h-4 w-4 text-slate-300 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate flex items-center gap-1.5">
                            <span className="truncate">{j.ref} · {j.title}</span>
                            {j.auto_billing && <span data-testid={`auto-badge-${j.id}`} className="text-[9px] bg-purple-100 text-purple-700 font-bold rounded px-1 py-0.5 flex-shrink-0">AUTO</span>}
                          </div>
                          <div className="text-xs text-slate-500 truncate">{j.customer_name}</div>
                          <div className="flex items-center justify-between mt-1.5">
                            <span className="text-xs font-semibold text-slate-700">{money(j.expected_value, cur)}</span>
                            {j.invoice_number && <span className="text-[10px] bg-slate-100 rounded px-1.5 py-0.5 text-slate-500">#{j.invoice_number}</span>}
                          </div>
                          {j.billing_missing?.length > 0 && stage === "Awaiting Approval" && (
                            <div className="text-[10px] text-amber-600 mt-1">{j.billing_missing.length} missing before invoicing</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {byStage(stage).length === 0 && <div className="text-center text-xs text-slate-300 py-6">Drop jobs here</div>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="auto" className="mt-4">
          <AutoBilling cur={cur} onGenerated={load} />
        </TabsContent>

        <TabsContent value="export" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-md p-3 mb-3 grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
            <div><Label className="text-xs text-slate-600">Date type</Label>
              <Select value={filters.date_type} onValueChange={(v) => setFilters((f) => ({ ...f, date_type: v }))}>
                <SelectTrigger data-testid="billing-date-type" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{DATE_TYPES.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs text-slate-600">From date</Label><Input data-testid="billing-from" type="date" value={filters.from_date} onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))} className="mt-1 bg-white" /></div>
            <div><Label className="text-xs text-slate-600">To date</Label><Input data-testid="billing-to" type="date" value={filters.to_date} onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))} className="mt-1 bg-white" /></div>
            <Button className="btn-accent" data-testid="billing-export-btn" onClick={doExport}><Download className="h-4 w-4 mr-1" />Download Billing CSV ({selectedIds.length})</Button>
          </div>

          <div className="bg-white border border-slate-200 rounded-md overflow-x-auto scroll-thin mb-4">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0"><tr>
                <th className="px-3 py-2.5"><Checkbox data-testid="billing-select-all" checked={exportRows.length > 0 && selectedIds.length === exportRows.length} onCheckedChange={(v) => { const n = {}; if (v) exportRows.forEach((r) => n[r.id] = true); setSel(n); }} /></th>
                {["Job No", "Completion", "Customer", "Site", "PO", "Tech", "Value", "Status"].map((h) => <th key={h} className="text-left font-medium text-slate-500 px-3 py-2.5 whitespace-nowrap">{h}</th>)}
              </tr></thead>
              <tbody>
                {exportRows.map((j) => (
                  <tr key={j.id} data-testid={`billing-row-${j.id}`} className="data-row border-t border-slate-100">
                    <td className="px-3 py-2.5"><Checkbox data-testid={`billing-check-${j.id}`} checked={!!sel[j.id]} onCheckedChange={(v) => setSel((s) => ({ ...s, [j.id]: v }))} /></td>
                    <td className="px-3 py-2.5 font-medium text-slate-800">{j.ref}</td>
                    <td className="px-3 py-2.5 text-slate-600">{fmtDate(j.completion_date)}</td>
                    <td className="px-3 py-2.5 text-slate-600">{j.customer_name}</td>
                    <td className="px-3 py-2.5 text-slate-600">{j.site_name}</td>
                    <td className="px-3 py-2.5 text-slate-500">{j.po_number || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-500">{j.technician_name || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-800">{money(j.expected_value, cur)}</td>
                    <td className="px-3 py-2.5"><StatusBadge status={j.billing_status} /></td>
                  </tr>
                ))}
                {exportRows.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-slate-400">No jobs match these filters.</td></tr>}
              </tbody>
            </table>
          </div>

          <h3 className="font-head font-semibold text-sm mb-2">Export History</h3>
          <div className="bg-white border border-slate-200 rounded-md overflow-x-auto scroll-thin">
            <table className="w-full text-sm">
              <thead className="bg-slate-50"><tr>{["Batch", "Range", "Jobs", "Value", "By", "Created", "Status"].map((h) => <th key={h} className="text-left font-medium text-slate-500 px-3 py-2.5 whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.id} data-testid={`batch-row-${b.id}`} className="border-t border-slate-100">
                    <td className="px-3 py-2.5 font-medium text-slate-800">{b.ref}</td>
                    <td className="px-3 py-2.5 text-slate-600">{fmtDate(b.from_date)} – {fmtDate(b.to_date)}</td>
                    <td className="px-3 py-2.5">{b.job_count}</td>
                    <td className="px-3 py-2.5 text-slate-800">{money(b.total_value, cur)}</td>
                    <td className="px-3 py-2.5 text-slate-600">{b.created_by}</td>
                    <td className="px-3 py-2.5 text-slate-500">{fmtDate(b.created_at)}</td>
                    <td className="px-3 py-2.5"><StatusBadge status={b.status} /></td>
                  </tr>
                ))}
                {batches.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-400">No export batches yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Invoice number dialog when moving to Invoiced */}
      <Dialog open={!!invoiceDlg} onOpenChange={(v) => !v && setInvoiceDlg(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-head">Mark as Invoiced</DialogTitle>
            <DialogDescription>An invoice number is required to move {invoiceDlg?.ref} to Invoiced. Amden only records this reference.</DialogDescription></DialogHeader>
          <div><Label className="text-xs text-slate-600">Invoice number</Label>
            <Input data-testid="invoice-number-input" value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} placeholder="e.g. INV-10442" className="mt-1" autoFocus /></div>
          <DialogFooter><Button variant="outline" onClick={() => setInvoiceDlg(null)}>Cancel</Button>
            <Button className="btn-accent" data-testid="invoice-confirm" onClick={() => { if (!invoiceNo.trim()) return toast.error("Invoice number required"); const j = invoiceDlg; setInvoiceDlg(null); move(j, "Invoiced", invoiceNo.trim()); }}>Mark Invoiced</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sent to accounts confirmation */}
      <Dialog open={!!confirmSent} onOpenChange={(v) => !v && confirmSentAction(false)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><Send className="h-4 w-4" />Has this export been sent to accounts?</DialogTitle>
            <DialogDescription>Batch {confirmSent?.ref} · {confirmSent?.job_count} jobs · {money(confirmSent?.total_value, cur)}.</DialogDescription></DialogHeader>
          <div><Label className="text-xs text-slate-600">Accounts contact (optional)</Label><Input data-testid="sent-contact" value={sentContact} onChange={(e) => setSentContact(e.target.value)} className="mt-1" /></div>
          <DialogFooter><Button variant="outline" data-testid="confirm-sent-no" onClick={() => confirmSentAction(false)}>Not yet</Button>
            <Button className="btn-accent" data-testid="confirm-sent-yes" onClick={() => confirmSentAction(true)}>Yes, sent</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
